import React, { ReactComponentElement } from "react";
import { EditorView } from "prosemirror-view";
import { classNamePrefix } from "@/utils/common";
import {
  Catalog,
  CatalogData,
  CatalogStyle,
  emptyCatalogData,
} from "./messageService";

const HEAD_LEVEL = 7;

const numberHeadingFactory = (minLevel: number) => {
  const numbers = [0, 0, 0, 0, 0, 0, 0];

  return (index: number) => {
    for (let i = 1; i < index; i++) {
      if (numbers[i] === 0) {
        numbers[i] = 1;
      }
    }

    numbers[index] += 1;

    for (let i = index + 1; i < HEAD_LEVEL; i++) {
      numbers[i] = 0;
    }

    return `${numbers.slice(minLevel, index + 1).join(".")}`;
  };
};

export const prefixFactory = (
  style: CatalogStyle,
  minLevel: number
): ((level: number) => ReactComponentElement<any>) => {
  const numberHeading =
    style === "number" ? numberHeadingFactory(minLevel) : () => "";

  let prefix: ReactComponentElement<any>;

  switch (style) {
    case "point":
      prefix = <div className={classNamePrefix("icon-round-preview")} />;
      break;
    case "circle":
      prefix = <div className={classNamePrefix("icon-ring-preview")} />;
      break;
    case "rect":
      prefix = <div className={classNamePrefix("icon-rect-preview")} />;
      break;
    default:
      prefix = null;
      break;
  }

  return (level: number): ReactComponentElement<any> => {
    if (style !== "number") {
      return prefix;
    }

    return <div className="number">{numberHeading(level)}&nbsp;</div>;
  };
};

export const getCatalogData = (view: EditorView): CatalogData => {
  if (!view) {
    return emptyCatalogData;
  }

  const $headingNodes = view.dom.querySelectorAll("h1, h2, h3, h4, h5, h6");
  const catalog: Catalog = [];

  let maxLevel = 0;
  let minLevel = HEAD_LEVEL;

  $headingNodes.forEach(($node) => {
    if ($node.textContent === null) {
      return;
    }

    const text = $node.textContent.replace(/\s/g, "").replace(/#/g, "");

    if (!text) {
      return;
    }

    const level = parseInt($node.nodeName.slice(1), 10);

    if (level > maxLevel) {
      maxLevel = level;
    }
    if (level < minLevel) {
      minLevel = level;
    }

    catalog.push({
      node: $node,
      key: $node.id,
      text: $node.textContent,
      level,
      isIntersecting: false,
      scrollIntoView: () => $node.scrollIntoView(),
    });
  });

  return { catalog, minLevel };
};
